import React, { useEffect, useState } from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import ThumbsUp from 'lucide-react/dist/esm/icons/thumbs-up';
import ThumbsDown from 'lucide-react/dist/esm/icons/thumbs-down';
import {
  TextField, TextArea, Button, Text, Badge, Spinner, EmptyState, IconButton, Select, useToast,
} from 'components/ui';
import { useUIStore } from 'stores';
import { getTranslation } from 'i18n';
import { useSalesStore } from '../../stores/salesStore';

const LeadExamplesManager: React.FC = () => {
  const { examples, examplesLoading, fetchExamples, createExample, deleteExample } = useSalesStore();
  const language = useUIStore((s) => s.language);
  const t = (key: string) => getTranslation(language, key);
  const toast = useToast();
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState<'good' | 'bad'>('good');
  const [companyUrl, setCompanyUrl] = useState('');
  const [explanation, setExplanation] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchExamples();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!companyUrl.trim() || !explanation.trim()) return;
    setSubmitting(true);
    try {
      await createExample({ type, company_url: companyUrl.trim(), explanation: explanation.trim() });
      setCompanyUrl('');
      setExplanation('');
      setShowForm(false);
      toast.success(t('salesExamplesToastAdded'));
    } catch {
      toast.error(t('salesExamplesErrorAdd'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteExample(id);
      toast.success(t('salesExamplesToastDeleted'));
    } catch {
      toast.error(t('salesExamplesErrorDelete'));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <Text variant="subheading">{t('salesExamplesTitle')}</Text>
          <p className="text-xs text-content-muted mt-0.5">
            {t('salesExamplesDesc')}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowForm(!showForm)}
          leftIcon={<Plus size={14} />}
        >
          {t('salesExamplesBtnAdd')}
        </Button>
      </div>

      {showForm && (
        <div className="p-4 rounded-lg bg-surface-primary border border-subtle mb-4 space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => setType('good')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors ${
                type === 'good'
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'bg-surface-secondary text-content-muted hover:text-content-secondary'
              }`}
            >
              <ThumbsUp size={14} /> {t('salesExamplesTypeGood')}
            </button>
            <button
              onClick={() => setType('bad')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors ${
                type === 'bad'
                  ? 'bg-red-500/15 text-red-400'
                  : 'bg-surface-secondary text-content-muted hover:text-content-secondary'
              }`}
            >
              <ThumbsDown size={14} /> {t('salesExamplesTypeBad')}
            </button>
          </div>
          <TextField
            label={t('salesExamplesLabelUrl')}
            value={companyUrl}
            onChange={(e) => setCompanyUrl(e.target.value)}
            placeholder={t('salesExamplesPlaceholderUrl')}
          />
          <TextArea
            label={t('salesExamplesLabelExplanation')}
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder={t('salesExamplesPlaceholderExplanation')}
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              {t('salesExamplesBtnCancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!companyUrl.trim() || !explanation.trim()}
            >
              {t('salesExamplesBtnSave')}
            </Button>
          </div>
        </div>
      )}

      {examplesLoading && examples.length === 0 ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : examples.length === 0 ? (
        <EmptyState
          title={t('salesExamplesEmpty')}
          description={t('salesExamplesEmptyDesc')}
        />
      ) : (
        <div className="space-y-2">
          {examples.map((ex) => (
            <div
              key={ex.id}
              className="flex items-start gap-3 p-3 rounded-lg bg-surface-primary border border-subtle"
            >
              <Badge
                variant={ex.type === 'good' ? 'success' : 'danger'}
                size="sm"
                className="mt-0.5 flex-shrink-0"
              >
                {ex.type === 'good' ? t('salesExamplesTypeGood') : t('salesExamplesTypeBad')}
              </Badge>
              <div className="flex-1 min-w-0">
                <a
                  href={ex.company_url.startsWith('http') ? ex.company_url : `https://${ex.company_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-brand hover:underline block truncate"
                >
                  {ex.company_url}
                </a>
                <p className="text-xs text-content-secondary mt-1 leading-relaxed">
                  {ex.explanation}
                </p>
              </div>
              <IconButton
                icon={<Trash2 size={14} />}
                variant="ghost"
                aria-label={t('salesExamplesAriaDelete')}
                onClick={() => handleDelete(ex.id)}
                className="flex-shrink-0 text-content-muted hover:text-status-error"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadExamplesManager;
